import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useTheme } from '../context/ThemeContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import {
  TicketPlus,
  AlertCircle,
  Clock,
  CheckCircle2,
  XCircle,
  TrendingUp,
  Users,
  Briefcase,
  Calendar,
  Zap,
  Gauge,
  Timer,
  Target,
} from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
} from 'recharts';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [vendorReport, setVendorReport] = useState([]);
  const [engineerReport, setEngineerReport] = useState([]);
  const [filteredMetrics, setFilteredMetrics] = useState(null);
  const [supportInsights, setSupportInsights] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [dateRange, setDateRange] = useState('24hr');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  const navigate = useNavigate();
  const { isDark } = useTheme();

  // Color scheme for charts - diverse colors for pie charts
  const COLORS = [
    '#6366f1', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ef4444',
    '#06b6d4', '#84cc16', '#f97316', '#8b5cf6', '#d946ef', '#0891b2', '#16a34a',
    '#dc2626', '#2563eb', '#7c3aed', '#db2777', '#ea580c', '#15803d', '#1e40af',
  ];

  // Helper function to convert Tailwind color classes to hex values
  const getColorValue = (tailwindClass) => {
    const colorMap = {
      'text-amber-400': '#fbbf24',
      'text-blue-400': '#60a5fa',
      'text-emerald-400': '#4ade80',
      'text-slate-300': '#cbd5e1',
    };
    return colorMap[tailwindClass] || '#ffffff';
  };

  // Helper function for tooltip styling based on theme
  const getTooltipStyle = () => {
    return {
      backgroundColor: isDark ? '#1e293b' : '#f8fafc',
      border: `1px solid ${isDark ? '#475569' : '#e2e8f0'}`,
      borderRadius: '6px',
      color: isDark ? '#f1f5f9' : '#1e293b',
    };
  };

  useEffect(() => {
    fetchInitialData();
  }, [dateRange, customStartDate, customEndDate]);

  // Auto-fetch filtered reports when product or date changes
  useEffect(() => {
    if (selectedProduct !== null) {
      fetchFilteredReports();
    }
  }, [selectedProduct, dateRange, customStartDate, customEndDate]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      const { startDate, endDate } = getDateRange();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      const [statsRes, productsRes, insightsRes] = await Promise.all([
        api.get(`/dashboard/stats?${params}`),
        api.get('/products'),
        api.get(`/dashboard/insights?${params}`),
      ]);
      setStats(statsRes.data);
      setProducts(productsRes.data);
      setSupportInsights(insightsRes.data);
    } catch (error) {
      console.error('Error fetching initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDateRange = useCallback(() => {
    const now = new Date();
    let startDate = null;

    if (dateRange === 'custom') {
      if (!customStartDate || !customEndDate) return { startDate: null, endDate: null };
      return { startDate: customStartDate, endDate: customEndDate };
    }

    const dateRangeMap = {
      '12hr': 12 * 60 * 60 * 1000,
      '24hr': 24 * 60 * 60 * 1000,
      '2days': 2 * 24 * 60 * 60 * 1000,
      '7days': 7 * 24 * 60 * 60 * 1000,
      '1month': 30 * 24 * 60 * 60 * 1000,
      '6months': 6 * 30 * 24 * 60 * 60 * 1000,
      '1year': 365 * 24 * 60 * 60 * 1000,
    };

    if (dateRangeMap[dateRange]) {
      startDate = new Date(now.getTime() - dateRangeMap[dateRange]);
    }

    return {
      startDate: startDate ? startDate.toISOString().split('T')[0] : null,
      endDate: now.toISOString().split('T')[0],
    };
  }, [dateRange, customStartDate, customEndDate]);

  const fetchFilteredReports = useCallback(async () => {
    if (selectedProduct === null) return;

    try {
      const params = new URLSearchParams();
      params.append('product_id', selectedProduct);
      
      const { startDate, endDate } = getDateRange();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);

      const [metricsRes, vendorRes, engineerRes, insightsRes] = await Promise.all([
        api.get(`/dashboard/metrics?${params}`),
        api.get(`/dashboard/vendor-report?${params}`),
        api.get(`/dashboard/engineer-report?${params}`),
        api.get(`/dashboard/insights?${params}`),
      ]);

      setFilteredMetrics(metricsRes.data);
      setVendorReport(vendorRes.data.data || []);
      setEngineerReport(engineerRes.data.data || []);
      setSupportInsights(insightsRes.data);
    } catch (error) {
      console.error('Error fetching filtered reports:', error);
    }
  }, [selectedProduct, dateRange, customStartDate, customEndDate]);

  const handleProductChange = (e) => {
    const value = e.target.value;
    setSelectedProduct(value ? parseInt(value) : null);
    if (!value) {
      setFilteredMetrics(null);
      setVendorReport([]);
      setEngineerReport([]);
    }
  };

  const handleDateChange = (e) => {
    const value = e.target.value;
    setDateRange(value);
    if (value !== 'custom') {
      setCustomStartDate('');
      setCustomEndDate('');
    }
  };

  const priorityData = stats?.priority_counts
    ? Object.entries(stats.priority_counts).map(([name, data]) => ({
        name,
        value: data.count,
        color: data.color,
      }))
    : [];

  const productData = stats?.product_counts
    ? Object.entries(stats.product_counts).map(([name, count], idx) => ({
        name,
        value: count,
        color: COLORS[idx % COLORS.length],
      }))
    : [];

  const statusData = filteredMetrics
    ? [
        { name: 'Open', value: filteredMetrics.open, color: '#f59e0b' },
        { name: 'In Progress', value: filteredMetrics.in_progress, color: '#3b82f6' },
        { name: 'Resolved', value: filteredMetrics.resolved, color: '#8b5cf6' },
        { name: 'Closed', value: filteredMetrics.closed, color: '#10b981' },
      ].filter(item => item.value > 0)
    : [];

  const metricsCards = [
    {
      title: 'Open Tickets',
      value: filteredMetrics?.open ?? stats?.open_tickets ?? 0,
      icon: AlertCircle,
      gradient: 'from-amber-500 to-orange-500',
      bgColor: 'bg-amber-50',
      iconColor: 'text-amber-600',
      darkIconColor: 'text-amber-400',
      darkTextColor: 'text-amber-400',
    },
    {
      title: 'In Progress',
      value: filteredMetrics?.in_progress ?? stats?.in_progress_tickets ?? 0,
      icon: Clock,
      gradient: 'from-blue-500 to-indigo-500',
      bgColor: 'bg-blue-50',
      iconColor: 'text-blue-600',
      darkIconColor: 'text-blue-400',
      darkTextColor: 'text-blue-400',
    },
    {
      title: 'Resolved',
      value: filteredMetrics?.resolved ?? stats?.resolved_tickets ?? 0,
      icon: CheckCircle2,
      gradient: 'from-emerald-500 to-green-500',
      bgColor: 'bg-emerald-50',
      iconColor: 'text-emerald-600',
      darkIconColor: 'text-emerald-400',
      darkTextColor: 'text-emerald-400',
    },
    {
      title: 'Closed',
      value: filteredMetrics?.closed ?? stats?.closed_tickets ?? 0,
      icon: XCircle,
      gradient: 'from-slate-500 to-slate-600',
      bgColor: 'bg-slate-50',
      iconColor: 'text-slate-600',
      darkIconColor: 'text-slate-300',
      darkTextColor: 'text-slate-300',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
      <div className="max-w-7xl mx-auto" data-testid="dashboard-page">
        {/* Header Section */}
        <div className="mb-3 sm:mb-4 md:mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 mb-2 sm:mb-3">
            <div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 dark:text-white tracking-tight">Team Dashboard</h1>
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 mt-1 sm:mt-2">Real-time insights into your ticket system</p>
            </div>
            <Button
              onClick={() => navigate('/create-ticket')}
              className="bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-700 hover:to-indigo-600 text-white font-medium shadow-md shadow-indigo-500/25 hover:shadow-lg transition-all py-1.5 sm:py-2 px-3 sm:px-4 text-xs sm:text-sm"
            >
              <TicketPlus className="mr-2 h-4 w-4" />
              Create Ticket
            </Button>
          </div>
        </div>

        {/* Product Filter Card */}
        <div className="mb-3 sm:mb-4 md:mb-6">
          <Card className="border-0 shadow-sm hover:shadow-md transition-shadow bg-white dark:bg-slate-900/80 dark:border-slate-800">
            <CardContent className="p-3 sm:p-4 md:p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
                {/* Product Filter */}
                <div>
                  <div className="flex items-center gap-2 mb-1.5 sm:mb-2">
                    <Briefcase className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-indigo-600" />
                    <label className="text-xs font-bold text-slate-700 dark:text-slate-200 uppercase tracking-wider">
                      Filter by Product
                    </label>
                  </div>
                  <select
                    value={selectedProduct || ''}
                    onChange={handleProductChange}
                    className="w-full px-2.5 sm:px-3 py-1.5 sm:py-2 border border-slate-200 dark:border-slate-700 dark:bg-slate-900 dark:text-white hover:border-indigo-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white text-xs text-slate-900 font-medium transition-all"
                  >
                    <option value="">All Products</option>
                    {products.map((product) => (
                      <option key={product.id} value={product.id}>
                        {product.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Date Filter */}
                <div>
                  <div className="flex items-center gap-2 mb-1.5 sm:mb-2">
                    <Calendar className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-indigo-600" />
                    <label className="text-xs font-bold text-slate-700 dark:text-slate-200 uppercase tracking-wider">
                      Filter by Date
                    </label>
                  </div>
                  <select
                    value={dateRange}
                    onChange={handleDateChange}
                    className="w-full px-2.5 sm:px-3 py-1.5 sm:py-2 border border-slate-200 dark:border-slate-700 dark:bg-slate-900 dark:text-white hover:border-indigo-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white text-xs text-slate-900 font-medium transition-all"
                  >
                    <option value="12hr">Last 12 Hours</option>
                    <option value="24hr">Last 24 Hours</option>
                    <option value="2days">Last 2 Days</option>
                    <option value="7days">Last 7 Days</option>
                    <option value="1month">Last 1 Month</option>
                    <option value="6months">Last 6 Months</option>
                    <option value="1year">Last 1 Year</option>
                    <option value="custom">Custom Range</option>
                  </select>
                </div>

                {/* Custom Date Range */}
                {dateRange === 'custom' && (
                  <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                    <div>
                      <label className="text-xs font-bold text-slate-700 dark:text-slate-200 uppercase tracking-wider">Start Date</label>
                      <input
                        type="date"
                        value={customStartDate}
                        onChange={(e) => setCustomStartDate(e.target.value)}
                        className="w-full mt-0.5 sm:mt-1 px-2.5 sm:px-3 py-1.5 sm:py-2 border border-slate-200 dark:border-slate-700 dark:bg-slate-900 dark:text-white hover:border-indigo-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white text-xs text-slate-900 font-medium transition-all"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-bold text-slate-700 dark:text-slate-200 uppercase tracking-wider">End Date</label>
                      <input
                        type="date"
                        value={customEndDate}
                        onChange={(e) => setCustomEndDate(e.target.value)}
                        className="w-full mt-0.5 sm:mt-1 px-2.5 sm:px-3 py-1.5 sm:py-2 border border-slate-200 dark:border-slate-700 dark:bg-slate-900 dark:text-white hover:border-indigo-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white text-xs text-slate-900 font-medium transition-all"
                      />
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* KPI Metrics Cards */}
        <div className="mb-3 sm:mb-4 md:mb-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
            {metricsCards.map((card) => {
              const Icon = card.icon;
              return (
                <Card
                  key={card.title}
                  className="border-0 shadow-sm hover:shadow-md transition-all hover:scale-105 transform duration-200 bg-white dark:bg-slate-900/80 dark:border-slate-800"
                >
                  <CardContent className="p-2 sm:p-3 md:p-4">
                    <div className="flex flex-col items-start gap-1.5 sm:gap-2">
                      <div className={`${card.bgColor} dark:bg-slate-800/50 p-1.5 sm:p-2 rounded-lg`}>
                        <Icon className={`h-3.5 w-3.5 sm:h-4 sm:w-4 ${card.iconColor}`} style={{color: isDark ? getColorValue(card.darkIconColor) : undefined}} strokeWidth={2.5} />
                      </div>
                      <div className="w-full">
                        <p className="text-xs font-medium text-slate-600 dark:text-slate-400">{card.title}</p>
                        <div className={`text-lg sm:text-xl md:text-2xl font-bold ${card.iconColor}`} style={{color: isDark ? getColorValue(card.darkTextColor) : undefined}}>
                          {loading ? '...' : card.value}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Support Insights Section */}
        {!loading && supportInsights && (
          <div className="mb-4">
            <h2 className="text-sm font-bold text-slate-900 dark:text-white mb-3 flex items-center gap-2 uppercase tracking-wide">
              <Zap className="h-4 w-4 text-yellow-600" />
              Support Insights & Performance
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {/* Ticket Closing Rate */}
              <Card className="border-0 shadow-md hover:shadow-lg transition-all bg-white dark:bg-slate-900/80 dark:border-slate-800">
                <CardContent className="p-4">
                  <div className="flex flex-col items-start gap-2">
                    <div className="bg-emerald-50 dark:bg-emerald-900/20 p-2 rounded-lg">
                      <Target className="h-4 w-4 text-emerald-600" strokeWidth={2.5} />
                    </div>
                    <div className="w-full">
                      <p className="text-xs font-medium text-slate-600 dark:text-slate-400">Closing Rate</p>
                      <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                        {supportInsights.closing_rate_percentage}%
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-500 mt-1">
                        {supportInsights.resolved_tickets_count}/{supportInsights.total_tickets_analyzed} resolved
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Average Resolution Time */}
              <Card className="border-0 shadow-md hover:shadow-lg transition-all bg-white dark:bg-slate-900/80 dark:border-slate-800">
                <CardContent className="p-4">
                  <div className="flex flex-col items-start gap-2">
                    <div className="bg-blue-50 dark:bg-blue-900/20 p-2 rounded-lg">
                      <Timer className="h-4 w-4 text-blue-600" strokeWidth={2.5} />
                    </div>
                    <div className="w-full">
                      <p className="text-xs font-medium text-slate-600 dark:text-slate-400">Avg Resolution</p>
                      <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                        {Math.round(supportInsights.average_resolution_time_hours)}h
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-500 mt-1">
                        {supportInsights.average_resolution_time_hours.toFixed(1)} hours avg
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Tickets Open 30+ Days */}
              <Card className="border-0 shadow-md hover:shadow-lg transition-all bg-white dark:bg-slate-900/80 dark:border-slate-800">
                <CardContent className="p-4">
                  <div className="flex flex-col items-start gap-2">
                    <div className="bg-red-50 dark:bg-red-900/20 p-2 rounded-lg">
                      <AlertCircle className="h-4 w-4 text-red-600" strokeWidth={2.5} />
                    </div>
                    <div className="w-full">
                      <p className="text-xs font-medium text-slate-600 dark:text-slate-400">Aging (30+ Days)</p>
                      <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                        {supportInsights.aging_tickets.open_30_plus_days}
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-500 mt-1">
                        Need attention
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Team Efficiency Score */}
              <Card className="border-0 shadow-md hover:shadow-lg transition-all bg-white dark:bg-slate-900/80 dark:border-slate-800">
                <CardContent className="p-4">
                  <div className="flex flex-col items-start gap-2">
                    <div className="bg-purple-50 dark:bg-purple-900/20 p-2 rounded-lg">
                      <Gauge className="h-4 w-4 text-purple-600" strokeWidth={2.5} />
                    </div>
                    <div className="w-full">
                      <p className="text-xs font-medium text-slate-600 dark:text-slate-400">Efficiency Score</p>
                      <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                        {supportInsights.team_efficiency_score}%
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-500 mt-1">
                        Team performance
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Aging Tickets Breakdown */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mt-3">
              <Card className="border-0 shadow-md hover:shadow-lg transition-all bg-white dark:bg-slate-900/80 dark:border-slate-800">
                <CardHeader className="pb-2 pt-3 px-4 border-b border-slate-100 dark:border-slate-700">
                  <CardTitle className="text-xs flex items-center gap-2 text-slate-900 dark:text-white">
                    <Clock className="h-3 w-3 text-slate-600 dark:text-slate-400" />
                    Aging Tickets Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-slate-600 dark:text-slate-400">0-7 Days</span>
                      <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">{supportInsights.aging_tickets.open_0_7_days}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-slate-600 dark:text-slate-400">7-30 Days</span>
                      <span className="text-sm font-bold text-amber-600 dark:text-amber-400">{supportInsights.aging_tickets.open_7_30_days}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-slate-600 dark:text-slate-400">30+ Days</span>
                      <span className="text-sm font-bold text-red-600 dark:text-red-400">{supportInsights.aging_tickets.open_30_plus_days}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Status-wise Average Time */}
              <Card className="border-0 shadow-md hover:shadow-lg transition-all bg-white dark:bg-slate-900/80 dark:border-slate-800">
                <CardHeader className="pb-2 pt-3 px-4 border-b border-slate-100 dark:border-slate-700">
                  <CardTitle className="text-xs flex items-center gap-2 text-slate-900 dark:text-white">
                    <TrendingUp className="h-3 w-3 text-slate-600 dark:text-slate-400" />
                    Avg Working Days in Status
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-slate-600 dark:text-slate-400">Open</span>
                      <span className="text-sm font-bold text-amber-600 dark:text-amber-400">{supportInsights.avg_days_in_status.open.toFixed(1)}d</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-slate-600 dark:text-slate-400">In Progress</span>
                      <span className="text-sm font-bold text-blue-600 dark:text-blue-400">{supportInsights.avg_days_in_status.in_progress.toFixed(1)}d</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-slate-600 dark:text-slate-400">Resolved</span>
                      <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">{supportInsights.avg_days_in_status.resolved.toFixed(1)}d</span>
                    </div>
                    <p className="text-xs text-slate-400 dark:text-slate-500 mt-2 pt-2 border-t border-slate-200 dark:border-slate-700">
                      Based on {supportInsights.working_hours_per_day}h working days
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Top Performers */}
              {supportInsights.top_performers.length > 0 && (
                <Card className="border-0 shadow-md hover:shadow-lg transition-all bg-white dark:bg-slate-900/80 dark:border-slate-800">
                  <CardHeader className="pb-2 pt-3 px-4 border-b border-slate-100 dark:border-slate-700">
                    <CardTitle className="text-xs flex items-center gap-2 text-slate-900 dark:text-white">
                      <Users className="h-3 w-3 text-slate-600 dark:text-slate-400" />
                      Top Performers
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4">
                    <div className="space-y-2">
                      {supportInsights.top_performers.slice(0, 3).map((performer, idx) => (
                        <div key={performer.engineer_id} className="flex justify-between items-start">
                          <div className="flex-1">
                            <p className="text-xs font-medium text-slate-700">{idx + 1}. {performer.engineer_name}</p>
                            <p className="text-xs text-slate-500">{performer.closing_rate}% closing rate</p>
                          </div>
                          <span className="text-xs font-bold text-indigo-600">{performer.efficiency_score}%</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}

        {/* Charts Section */}
        {!loading && (
          <div className="mb-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {/* Priority Distribution */}
              {priorityData.length > 0 && (
                <Card className="border-0 shadow-md hover:shadow-lg transition-shadow bg-white dark:bg-slate-900/80 dark:border-slate-800">
                  <CardHeader className="pb-3 pt-4 px-4 border-b border-slate-100 dark:border-slate-700">
                    <CardTitle className="text-sm flex items-center gap-2 text-slate-900 dark:text-white">
                      <TrendingUp className="h-4 w-4 text-indigo-600" />
                      Priority Distribution
                    </CardTitle>
                    <CardDescription className="mt-1 text-xs text-slate-600 dark:text-slate-400">Grouped by priority</CardDescription>
                  </CardHeader>
                  <CardContent className="p-4">
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie
                          data={priorityData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, value }) => `${name}: ${value}`}
                          outerRadius={60}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {priorityData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px' }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Product Distribution */}
              {!selectedProduct && productData.length > 0 && (
                <Card className="border-0 shadow-md hover:shadow-lg transition-shadow bg-white dark:bg-slate-900/80 dark:border-slate-800">
                  <CardHeader className="pb-3 pt-4 px-4 border-b border-slate-100 dark:border-slate-700">
                    <CardTitle className="text-sm flex items-center gap-2 text-slate-900 dark:text-white">
                      <Briefcase className="h-4 w-4 text-indigo-600" />
                      Product Distribution
                    </CardTitle>
                    <CardDescription className="mt-1 text-xs text-slate-600 dark:text-slate-400">Grouped by product</CardDescription>
                  </CardHeader>
                  <CardContent className="p-4">
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie
                          data={productData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, value }) => `${name}: ${value}`}
                          outerRadius={60}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {productData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px' }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Status Distribution for Selected Product */}
              {selectedProduct && statusData.length > 0 && (
                <Card className="border-0 shadow-md hover:shadow-lg transition-shadow bg-white dark:bg-slate-900/80 dark:border-slate-800">
                  <CardHeader className="pb-3 pt-4 px-4 border-b border-slate-100 dark:border-slate-700">
                    <CardTitle className="text-sm flex items-center gap-2 text-slate-900 dark:text-white">
                      <Clock className="h-4 w-4 text-emerald-600" />
                      Ticket Status
                    </CardTitle>
                    <CardDescription className="mt-1 text-xs text-slate-600 dark:text-slate-400">Status breakdown</CardDescription>
                  </CardHeader>
                  <CardContent className="p-4">
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie
                          data={statusData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, value }) => `${name}: ${value}`}
                          outerRadius={60}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {statusData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px' }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}

        {/* Vendor/Reporter Report */}
        {vendorReport.length > 0 && (
          <div className="mb-4">
            <h2 className="text-sm font-bold text-slate-900 dark:text-white mb-3 flex items-center gap-2 uppercase tracking-wide">
              <Users className="h-4 w-4 text-purple-600" />
              Client Users / Vendors
            </h2>
            
            {/* Client User Metrics Table */}
            <Card className="border-0 shadow-md hover:shadow-lg transition-shadow bg-white dark:bg-slate-900/80 dark:border-slate-800 mb-4">
              <CardHeader className="pb-3 pt-4 px-4 border-b border-slate-100 dark:border-slate-700">
                <CardTitle className="text-sm flex items-center gap-2 text-slate-900 dark:text-white">
                  Ticket Metrics by Client
                </CardTitle>
                <CardDescription className="mt-1 text-xs text-slate-600">Status breakdown and ticket counts</CardDescription>
              </CardHeader>
              <CardContent className="p-4">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-slate-200 dark:border-slate-700">
                        <th className="text-left py-2 px-3 font-semibold text-slate-700 dark:text-slate-200">Client Name</th>
                        <th className="text-center py-2 px-2 font-semibold text-slate-700 dark:text-slate-200">Raised</th>
                        <th className="text-center py-2 px-2 font-semibold text-amber-600 dark:text-amber-400">Open</th>
                        <th className="text-center py-2 px-2 font-semibold text-blue-600 dark:text-blue-400">In Progress</th>
                        <th className="text-center py-2 px-2 font-semibold text-purple-600 dark:text-purple-400">Resolved</th>
                        <th className="text-center py-2 px-2 font-semibold text-emerald-600 dark:text-emerald-400">Closed</th>
                        <th className="text-center py-2 px-2 font-semibold text-slate-700 dark:text-slate-200">Closing Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vendorReport.map((vendor) => {
                        const closingRate = vendor.raised > 0 
                          ? ((vendor.closed + vendor.resolved) / vendor.raised * 100).toFixed(1)
                          : 0;
                        return (
                          <tr key={vendor.vendor_id} className="border-b border-slate-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                            <td className="py-3 px-3 font-medium text-slate-900 dark:text-white">{vendor.vendor_name}</td>
                            <td className="text-center py-3 px-2">
                              <span className="bg-indigo-100 text-indigo-700 px-2 py-1 rounded font-semibold">
                                {vendor.raised}
                              </span>
                            </td>
                            <td className="text-center py-3 px-2">
                              <span className="bg-amber-100 text-amber-700 px-2 py-1 rounded font-semibold">
                                {vendor.open}
                              </span>
                            </td>
                            <td className="text-center py-3 px-2">
                              <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded font-semibold">
                                {vendor.in_progress}
                              </span>
                            </td>
                            <td className="text-center py-3 px-2">
                              <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded font-semibold">
                                {vendor.resolved}
                              </span>
                            </td>
                            <td className="text-center py-3 px-2">
                              <span className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded font-semibold">
                                {vendor.closed}
                              </span>
                            </td>
                            <td className="text-center py-3 px-2 font-semibold">
                              <span className={closingRate > 70 ? 'text-emerald-600' : closingRate > 40 ? 'text-amber-600' : 'text-red-600'}>
                                {closingRate}%
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Client User Insights */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
              {/* Most Productive Client */}
              <Card className="border-0 shadow-md hover:shadow-lg transition-all bg-white dark:bg-slate-900/80 dark:border-slate-800">
                <CardHeader className="pb-2 pt-3 px-4 border-b border-slate-100 dark:border-slate-700">
                  <CardTitle className="text-xs flex items-center gap-2 text-slate-900 dark:text-white">
                    <TrendingUp className="h-3 w-3 text-slate-600 dark:text-slate-400" />
                    Most Productive
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  {vendorReport.length > 0 && (() => {
                    const mostProductive = vendorReport.reduce((a, b) => a.raised > b.raised ? a : b);
                    return (
                      <div>
                        <p className="text-sm font-bold text-slate-900 dark:text-white">{mostProductive.vendor_name}</p>
                        <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400 mt-1">{mostProductive.raised}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">tickets raised</p>
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>

              {/* Highest Open Tickets */}
              <Card className="border-0 shadow-md hover:shadow-lg transition-all bg-white dark:bg-slate-900/80 dark:border-slate-800">
                <CardHeader className="pb-2 pt-3 px-4 border-b border-slate-100 dark:border-slate-700">
                  <CardTitle className="text-xs flex items-center gap-2 text-slate-900 dark:text-white">
                    <AlertCircle className="h-3 w-3 text-slate-600 dark:text-slate-400" />
                    Highest Open
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  {vendorReport.length > 0 && (() => {
                    const highestOpen = vendorReport.reduce((a, b) => a.open > b.open ? a : b);
                    return (
                      <div>
                        <p className="text-sm font-bold text-slate-900 dark:text-white">{highestOpen.vendor_name}</p>
                        <p className="text-2xl font-bold text-amber-600 dark:text-amber-400 mt-1">{highestOpen.open}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">open tickets</p>
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>

              {/* Best Closing Rate */}
              <Card className="border-0 shadow-md hover:shadow-lg transition-all bg-white dark:bg-slate-900/80 dark:border-slate-800">
                <CardHeader className="pb-2 pt-3 px-4 border-b border-slate-100 dark:border-slate-700">
                  <CardTitle className="text-xs flex items-center gap-2 text-slate-900 dark:text-white">
                    <Target className="h-3 w-3 text-slate-600 dark:text-slate-400" />
                    Best Closing Rate
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  {vendorReport.length > 0 && (() => {
                    const withClosingRate = vendorReport.map(v => ({
                      ...v,
                      closingRate: v.raised > 0 ? ((v.closed + v.resolved) / v.raised * 100) : 0
                    }));
                    const bestClosing = withClosingRate.reduce((a, b) => a.closingRate > b.closingRate ? a : b);
                    return (
                      <div>
                        <p className="text-sm font-bold text-slate-900">{bestClosing.vendor_name}</p>
                        <p className="text-2xl font-bold text-emerald-600 mt-1">{bestClosing.closingRate.toFixed(1)}%</p>
                        <p className="text-xs text-slate-500 mt-2">resolution rate</p>
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            </div>

            {/* Engineer Task Distribution & Unassigned Tasks */}
            {(() => {
              const totalAssigned = engineerReport.reduce((sum, eng) => sum + eng.assigned, 0);
              const unassignedCount = stats?.unassigned_tickets || 0;
              return (
                <div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                    {/* Total Assigned Tasks */}
                    <Card className="border-0 shadow-sm bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="text-xs text-slate-600 dark:text-slate-300 font-medium">Total Assigned</p>
                            <p className="text-2xl font-bold text-blue-600 dark:text-blue-400 mt-1">{totalAssigned}</p>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">tasks to engineers</p>
                          </div>
                          <Briefcase className="h-5 w-5 text-blue-500 dark:text-blue-400 opacity-50" />
                        </div>
                      </CardContent>
                    </Card>

                    {/* Unassigned Tasks - Alert if any */}
                    <Card className={`border-0 shadow-sm transition-shadow ${
                      unassignedCount > 0 
                        ? 'bg-gradient-to-br from-red-50 to-red-100 dark:from-red-950 dark:to-red-900 hover:shadow-md' 
                        : 'bg-gradient-to-br from-green-50 to-green-100 dark:from-green-950 dark:to-green-900'
                    }`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="text-xs text-slate-600 dark:text-slate-300 font-medium">Unassigned</p>
                            <p className={`text-2xl font-bold mt-1 ${unassignedCount > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                              {unassignedCount}
                            </p>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">waiting for assignment</p>
                          </div>
                          <AlertCircle className={`h-5 w-5 opacity-50 ${unassignedCount > 0 ? 'text-red-500 dark:text-red-400' : 'text-green-500 dark:text-green-400'}`} />
                        </div>
                      </CardContent>
                    </Card>

                    {/* Assignment Rate */}
                    <Card className="border-0 shadow-sm bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-950 dark:to-purple-900 hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="text-xs text-slate-600 dark:text-slate-300 font-medium">Assignment Rate</p>
                            <p className="text-2xl font-bold text-purple-600 dark:text-purple-400 mt-1">
                              {stats?.total_tickets > 0 ? ((totalAssigned / stats.total_tickets) * 100).toFixed(0) : 0}%
                            </p>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">of tasks assigned</p>
                          </div>
                          <Target className="h-5 w-5 text-purple-500 dark:text-purple-400 opacity-50" />
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Engineer Workload Distribution Chart */}
                  <Card className="border-0 shadow-md hover:shadow-lg transition-shadow bg-white dark:bg-slate-900/80 dark:border-slate-800">
                    <CardHeader className="pb-3 pt-4 px-4 border-b border-slate-100 dark:border-slate-700">
                      <CardTitle className="text-sm flex items-center gap-2 text-slate-900 dark:text-white">
                        <Users className="h-4 w-4 text-blue-600" />
                        Task Distribution to Engineers
                      </CardTitle>
                      <CardDescription className="mt-1 text-xs text-slate-600 dark:text-slate-400">Workload allocation and status breakdown</CardDescription>
                    </CardHeader>
                    <CardContent className="p-4">
                      <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={engineerReport} margin={{ top: 10, right: 15, left: 0, bottom: 50 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                          <XAxis dataKey="engineer_name" angle={-45} textAnchor="end" height={60} tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 10 }} />
                          <Tooltip contentStyle={getTooltipStyle()} />
                          <Legend wrapperStyle={{ paddingTop: '8px', fontSize: '12px' }} />
                          <Bar dataKey="assigned" fill={COLORS[5]} name="Assigned" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="open" fill={COLORS[8]} name="Open" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="in_progress" fill={COLORS[2]} name="In Progress" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="resolved" fill={COLORS[9]} name="Resolved" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="closed" fill={COLORS[3]} name="Closed" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </div>
              );
            })()}
          </div>
        )}

        {/* Engineer/Assignee Report */}
        {engineerReport.length > 0 && (
          <div>
            <Card className="border-0 shadow-md hover:shadow-lg transition-shadow bg-white dark:bg-slate-900/80 dark:border-slate-800">
              <CardHeader className="pb-3 pt-4 px-4 border-b border-slate-100 dark:border-slate-700">
                <CardTitle className="text-sm flex items-center gap-2 text-slate-900 dark:text-white">
                  <Users className="h-4 w-4 text-emerald-600" />
                  Engineer/Assignee Workload
                </CardTitle>
                <CardDescription className="mt-1 text-xs text-slate-600 dark:text-slate-400">Tickets by engineer</CardDescription>
              </CardHeader>
              <CardContent className="p-4">
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={engineerReport} margin={{ top: 10, right: 15, left: 0, bottom: 50 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis dataKey="engineer_name" angle={-45} textAnchor="end" height={60} tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip contentStyle={getTooltipStyle()} />
                    <Legend wrapperStyle={{ paddingTop: '8px', fontSize: '12px' }} />
                    <Bar dataKey="assigned" fill={COLORS[0]} name="Assigned" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="in_progress" fill={COLORS[1]} name="In Progress" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="resolved" fill={COLORS[2]} name="Resolved" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="closed" fill={COLORS[3]} name="Closed" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
